/**
 * CanvasserHome — a canvasser's home page (Oct 27, 2026).
 *
 * Greg, after adding Michael Lewis: "when he logs in, i want it to go to home
 * page and show assigned areas and to log a lead manually. also show 'my pay'
 * board", then "his home can also show his kpi's and goal for kpi's".
 * Canvassers used to be sent straight to the full-screen canvas, which has no
 * header, so they had no way to sign out.
 *
 * One page, read-only except for the location check:
 *   • Open the canvas · Log a lead (by address, same approval and pay as an
 *     App from the map) · My Pay
 *   • whether location is on for this phone, with the steps when it isn't
 *   • his numbers today / this week (hours, doors, contacts, appointments)
 *     against the goals that exist for a canvasser: today's appointment goal
 *     from the 10am call, the weekly appointment goal from Crew → Set goals,
 *     the 1-appointment-per-2-hours minimum, and the shift blocks planned at
 *     his 1-on-1
 *   • today's confirmed plan (blocks, area, team knock), his assigned areas,
 *     and this week's pay so far
 *
 * Every number comes from the same sources that pay and coach him:
 * canvasser_my_pay (sessions, appointments, fees), daily_commitments,
 * crew_goals, weekly_commitment_match, canvas_area_stats.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import { mondayOf } from "../../lib/weeklySpiffs";
import { aggregateMatch, type CommitmentMatchRow } from "../../lib/commitmentMath";
import { fetchMyCommitment, todayET, type DailyCommitment } from "../../lib/dailyCommitments";
import { logQuickAction } from "../../lib/uiEvents";
import MyDayCard from "../home/MyDayCard";
import MyAreasWidget from "../home/widgets/MyAreasWidget";
import { LocationCheckCard } from "../canvas/LocationHelp";

interface PayKpis {
  rates: { min_per_hour: number };
  open_session_started_at: string | null;
  today: { hours: number; doors: number; contacts: number; apps_set: number };
  week: {
    hours: number;
    sessions: Array<{ doors: number; contacts: number }>;
    apps_set: number;
    apps_approved: number;
    apps_pending: number;
    est_total: number;
    apps_per_2h: number | null;
    needed: number;
    below_min: boolean;
  };
  totals: { awaiting_approval: number; approved_unpaid: number };
}

const n = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);
const hrs = (v: number) => (Math.round(v * 10) / 10).toFixed(1);
const pct = (num: number, den: number) => (den > 0 ? `${Math.round((num / den) * 100)}%` : "—");
const plural = (k: number, one: string, many = one + "s") => `${k} ${k === 1 ? one : many}`;
function etTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" });
}
function etLongDay(): string {
  return new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "short", day: "numeric" });
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-2.5 text-center">
      <div className="text-xl font-extrabold tabular-nums text-bardin-ink">{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      {detail && <div className="mt-0.5 text-[10px] text-slate-400">{detail}</div>}
    </div>
  );
}

/** One goal: label, "x of y", a bar, and a line of context. */
function GoalRow({ label, value, ratio, tone, note }: { label: string; value: string; ratio: number | null; tone: "green" | "amber" | "slate"; note?: ReactNode }) {
  const bar = tone === "green" ? "bg-emerald-500" : tone === "amber" ? "bg-amber-400" : "bg-slate-300";
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        <span className="text-xs font-bold tabular-nums text-bardin-ink">{value}</span>
      </div>
      {ratio != null && (
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${bar}`} style={{ width: `${Math.max(3, Math.min(100, ratio * 100))}%` }} />
        </div>
      )}
      {note && <div className="mt-0.5 text-[11px] text-slate-500">{note}</div>}
    </div>
  );
}

export default function CanvasserHome({ memberId, firstName, preview = false }: { memberId: string; firstName: string; preview?: boolean }) {
  const [kpis, setKpis] = useState<PayKpis | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [plan, setPlan] = useState<DailyCommitment | null>(null);
  const [weekGoal, setWeekGoal] = useState<number | null>(null);
  const [blocks, setBlocks] = useState<{ committed: number; showed: number } | null>(null);
  const [span, setSpan] = useState<"today" | "week">("today");

  const day = todayET();
  const weekStart = mondayOf();

  const load = useCallback(async () => {
    const [pay, dc, goal, match] = await Promise.all([
      supabase.rpc("canvasser_my_pay", { p_member: memberId }),
      fetchMyCommitment(memberId, day).catch(() => null),
      supabase.from("crew_goals").select("target")
        .eq("scope", "personal").eq("period", "week").eq("metric", "apps")
        .eq("user_id", memberId).eq("period_start", weekStart)
        .order("created_at", { ascending: false }).limit(1),
      supabase.from("weekly_commitment_match").select("*").eq("user_id", memberId).eq("week_start", weekStart),
    ]);
    if (pay.error) setErr(pay.error.message);
    else { setErr(null); setKpis(pay.data as PayKpis); }
    setPlan(dc);
    const t = goal.data?.[0]?.target;
    setWeekGoal(t != null && n(t) > 0 ? n(t) : null);
    if (!match.error && match.data) {
      const m = aggregateMatch(match.data as CommitmentMatchRow[]);
      setBlocks(m.committed > 0 ? { committed: m.committed, showed: m.showed } : null);
    }
  }, [memberId, day, weekStart]);

  useEffect(() => { void load(); }, [load]);

  // Shifts, appointments, plans and goals change through the day.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bump = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void load(); }, 1500);
  }, [load]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useRealtimeTable("canvas_shifts", bump);
  useRealtimeTable("pending_canvas_appointments", bump);
  useRealtimeTable("daily_commitments", bump);
  useRealtimeTable("crew_goals", bump);
  useRealtimeTable("weekly_commitments", bump);

  const today = kpis?.today;
  const week = kpis?.week;
  const minPerHour = n(kpis?.rates.min_per_hour) || 0.5;
  const weekDoors = (week?.sessions ?? []).reduce((s, x) => s + n(x.doors), 0);
  const weekContacts = (week?.sessions ?? []).reduce((s, x) => s + n(x.contacts), 0);

  const isToday = span === "today";
  const hours = n(isToday ? today?.hours : week?.hours);
  const doors = isToday ? n(today?.doors) : weekDoors;
  const contacts = isToday ? n(today?.contacts) : weekContacts;
  const apps = n(isToday ? today?.apps_set : week?.apps_set);

  // Appointment goal: today's from the 10am call, the week's from Crew goals.
  let apptGoal: ReactNode;
  if (isToday) {
    if (plan?.is_off) {
      apptGoal = <GoalRow label="Appointments" value="Off today" ratio={null} tone="slate" />;
    } else if (plan && n(plan.goal_apps) > 0) {
      const g = n(plan.goal_apps);
      apptGoal = <GoalRow label="Appointments" value={`${apps} of ${g}`} ratio={apps / g} tone={apps >= g ? "green" : "amber"} note="Today's goal, set on your morning call." />;
    } else {
      apptGoal = <GoalRow label="Appointments" value={String(apps)} ratio={null} tone="slate" note="No appointment goal for today yet. Your TL or ASM sets it on the morning call." />;
    }
  } else if (weekGoal != null) {
    apptGoal = <GoalRow label="Appointments" value={`${apps} of ${weekGoal}`} ratio={apps / weekGoal} tone={apps >= weekGoal ? "green" : "amber"} note={`This week's goal${week ? ` · ${week.apps_approved} approved, ${week.apps_pending} waiting` : ""}.`} />;
  } else {
    apptGoal = <GoalRow label="Appointments" value={String(apps)} ratio={null} tone="slate" note="No weekly goal yet. Your TL or ASM sets it in Crew → Set goals." />;
  }

  // The 1-per-2-hours minimum (canvasser_min_appts_per_hour).
  const needed = isToday ? Math.ceil(hours * minPerHour) : n(week?.needed);
  const below = isToday ? hours >= 1 && apps < hours * minPerHour : !!week?.below_min;
  const per2h = hours > 0 ? (apps / hours) * 2 : null;
  const paceRow = hours < 1
    ? <GoalRow label="Pace" value="1 per 2 hrs" ratio={null} tone="slate" note={`The minimum is 1 appointment for every 2 hours on shift. It's checked once you've worked an hour ${isToday ? "today" : "this week"}.`} />
    : <GoalRow
        label="Pace"
        value={per2h != null ? `${per2h.toFixed(1)} per 2 hrs` : "—"}
        ratio={needed > 0 ? apps / needed : 1}
        tone={below ? "amber" : "green"}
        note={below
          ? `${plural(apps, "appointment")} in ${hrs(hours)} hrs. The minimum is ${needed} for ${hrs(hours)} hrs.`
          : `At or above the minimum of 1 per 2 hours (${needed} for ${hrs(hours)} hrs).`}
      />;

  const onTheWay = n(kpis?.totals.awaiting_approval) + n(kpis?.totals.approved_unpaid);

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {preview && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          🔭 <strong>Preview</strong>: {firstName}'s canvasser home, as he sees it. The location check is left out because it reads your device.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-bardin-ink">Welcome, {firstName}</h1>
          <p className="text-sm text-slate-500">{etLongDay()}</p>
        </div>
        {kpis && (kpis.open_session_started_at ? (
          <span className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> On shift since {etTime(kpis.open_session_started_at)}
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-500">Off shift</span>
        ))}
      </div>

      {/* The three things he does from here. */}
      <div className="mb-4 grid grid-cols-2 gap-2">
        <Link
          to="/canvas"
          onClick={() => logQuickAction("canvasser_open_canvas")}
          className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-bardin-orange px-4 py-3 text-base font-bold text-white shadow-sm hover:opacity-90"
        >
          🗺 Open the canvas
        </Link>
        <Link
          to="/canvas/appt?manual=1"
          onClick={() => logQuickAction("canvasser_log_lead")}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-bardin-ink shadow-sm hover:bg-slate-50"
        >
          ✍️ Log a lead
        </Link>
        <Link
          to="/my-pay"
          onClick={() => logQuickAction("canvasser_my_pay")}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-bardin-ink shadow-sm hover:bg-slate-50"
        >
          💰 My Pay
        </Link>
      </div>

      {!preview && <LocationCheckCard />}

      {/* KPIs + goals */}
      <section className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Your numbers">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Your numbers</div>
          <div className="flex rounded-full bg-slate-100 p-0.5 text-[11px] font-semibold">
            {(["today", "week"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setSpan(k)}
                className={`rounded-full px-3 py-1 ${span === k ? "bg-white text-bardin-ink shadow-sm" : "text-slate-500"}`}
                aria-pressed={span === k}
              >
                {k === "today" ? "Today" : "This week"}
              </button>
            ))}
          </div>
        </div>

        {err ? (
          <div className="rounded-lg bg-red-50 p-3 text-xs text-red-700">Couldn't load your numbers: {err}</div>
        ) : !kpis ? (
          <div className="py-6 text-center text-xs text-slate-400">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-2">
              <Kpi label="Hours" value={hrs(hours)} />
              <Kpi label="Doors" value={String(doors)} />
              <Kpi label="Contacts" value={String(contacts)} detail={pct(contacts, doors)} />
              <Kpi label="Appts" value={String(apps)} detail={pct(apps, contacts)} />
            </div>
            <div className="mt-1 text-right text-[10px] text-slate-400">% = of doors · of contacts</div>

            <div className="mt-3 space-y-3 border-t border-slate-100 pt-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Goals</div>
              {apptGoal}
              {paceRow}
              {!isToday && blocks && (
                <GoalRow
                  label="Shifts you planned"
                  value={`${blocks.showed} of ${blocks.committed} blocks`}
                  ratio={blocks.showed / blocks.committed}
                  tone={blocks.showed >= blocks.committed ? "green" : "amber"}
                  note="AM / PM / evening blocks from your 1-on-1, kept when you're on shift in them."
                />
              )}
            </div>
            {hours === 0 && isToday && !kpis.open_session_started_at && (
              <p className="mt-3 text-xs text-slate-500">
                Only time on shift counts. When you start knocking, open the canvas and tap the <strong>Off shift</strong> button at the top to go on shift.
              </p>
            )}
          </>
        )}
      </section>

      {/* Today's confirmed plan: blocks, area, team knock (goals are above). */}
      <MyDayCard meId={memberId} hideGoals readOnly={preview} />

      {/* Assigned areas — renders nothing when he has none. */}
      <div className="mb-4 empty:hidden">
        <MyAreasWidget memberId={memberId} />
      </div>

      {/* Pay snapshot */}
      {kpis && week && (
        <Link
          to="/my-pay"
          className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm hover:bg-slate-50"
        >
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">My Pay</div>
            <div className="text-xl font-extrabold text-bardin-teal">{fmtUSD(n(week.est_total))}</div>
            <div className="text-[11px] text-slate-500">
              this week so far (estimate){onTheWay > 0 ? ` · ${fmtUSD(onTheWay)} on the way` : ""}
            </div>
          </div>
          <span className="text-sm font-semibold text-bardin-teal">Open →</span>
        </Link>
      )}
    </div>
  );
}
